import { BookkeepingAccountantTaskTemplateName } from 'modules/HubV2/Bookkeeping/AccountantV2/constants';
import routes from 'modules/HubV2/Bookkeeping/common/routes';
import { TransactionExtractionTaskTemplateName } from 'modules/HubV2/Bookkeeping/TransactionExtraction/constants';
import { TaxTaskTemplateName } from 'modules/HubV2/Tax/constants';
import BACK_BOOKS_REFUND_QUEUE_PATH from 'modules/HubV2/OnboardingV2/BackBooksRefund/constants';

// derived from the database
export enum OnboardingTaskTemplateName {
    // Formation Tasks
    FILE_LLC = 'file_llc',
    CONFIRM_LLC = 'confirm_llc',
    FILE_EIN = 'file_ein',
    RESPOND_TO_FORMATION_CASE = 'respond_to_formation_case',
    REVIEW_FORMATION_APPLICATION = 'review_formation_application',
    COMPLETE_APPLICATION = 'complete_application',

    // Takeover Exceptions
    MEMBER_ENTITY_TAKEOVER_DQ = 'member_entity_takeover_dq',
    MEMBER_ENTITY_TAKEOVER_BLOCKED = 'member_entity_takeover_blocked',
    MEMBER_ENTITY_TAKEOVER_MIDDESK_IRS_UNAVAILABLE = 'member_entity_takeover_middesk_irs_unavailable',

    // Middesk Review Tasks
    REVIEW_MIDDESK_EXCEPTION = 'review_middesk_exception',

    // Formation Exceptions
    MEMBER_ENTITY_FORMATION_DQ = 'member_entity_formation_dq',
    MEMBER_ENTITY_FORMATION_BLOCKED = 'member_entity_formation_blocked',

    // S Corp Filing Tasks
    REVIEW_SCORP_ELECTION_DOCS = 'review_scorp_election_docs',
    FAX_SCORP_ELECTION_DOCS = 'fax_scorp_election_docs',
    CONFIRM_SCORP_ELECTION_DOCS_FAX = 'confirm_scorp_election_docs_fax',

    // Onboarding Accountant Tasks
    ESTABLISH_BEGINNING_BALANCE = 'establish_beginning_balance',

    // Payroll V2 Tasks
    BENEFITS_ON_PAYROLL = 'benefits_on_payroll',
    GUSTO_REVIEW = 'gusto_review',
    PAYROLL_MIGRATION = 'payroll_migration',

    // Payroll V2 / Employer Registration task framework tasks (backend PR #10156)
    WAITING_TO_PROCESS_PAYROLL = 'waiting_to_process_payroll',
    PAYROLL_PROCESSED_ER_DECISION = 'payroll_processed_employer_registration_decision',
    ER_CHECKPOINT = 'employer_registration_checkpoint',
    ER_NOT_REQUIRED = 'employer_registration_not_required',
    ER_IN_PROCESS = 'employer_registration_in_process',
    ER_AWAITING_MEMBER = 'employer_registration_awaiting_member',
    ER_WAITING_FOR_ID = 'employer_registration_waiting_for_id',
    ER_TPA = 'employer_registration_tpa',
    ER_CLOSE = 'employer_registration_close',

    // Takeover Tasks
    COMPLETE_ENTITY_TAKEOVER_APPLICATION = 'complete_takeover_application',
    VERIFY_EIN_CONFIRMATION_LETTER = 'verify_ein_confirmation_letter',
    VERIFY_SCORP_APPROVAL_LETTER = 'verify_scorp_approval_letter',
    VERIFY_BUSINESS = 'verify_business',
    VERIFY_ARTICLES_OF_ORGANIZATION = 'verify_articles_of_organization',
    // Named for the books rather than the election: back-books sells to every user type while
    // Retro-S is LLC-SC only, so a member who never files a 2553 can still be owed a refund.
    REFUND_BACK_BOOKS = 'refund_back_books',

    // Document Verification
    VERIFY_DOCUMENT_EXTRACTION = 'verify_document_extraction',
}

export const QUEUE_PATH_FOR_TASK: Record<
    | OnboardingTaskTemplateName
    | TaxTaskTemplateName
    | BookkeepingAccountantTaskTemplateName
    | TransactionExtractionTaskTemplateName,
    string
> = {
    // Bookkeeping Accountant tasks
    [BookkeepingAccountantTaskTemplateName.PENDING_CLOSE_CREATION_FAILED]: routes.accountantV2.queue,
    // Statement transaction extraction review. `verify_document_extraction` is the unwired sibling
    // built for flat single-field document checks; it has no queue of its own.
    [TransactionExtractionTaskTemplateName.STATEMENT_TRANSACTION_REVIEW]: routes.transactionExtraction.queue,
    [OnboardingTaskTemplateName.VERIFY_DOCUMENT_EXTRACTION]: '/hub-v2',
    // onboarding accountant
    [OnboardingTaskTemplateName.ESTABLISH_BEGINNING_BALANCE]: '/hub-v2/onboarding-v2/onboarding-account-v2/queue',
    // payroll v2
    [OnboardingTaskTemplateName.BENEFITS_ON_PAYROLL]: '/hub-v2/onboarding-v2/onboarding-payroll-v2/queue',
    [OnboardingTaskTemplateName.GUSTO_REVIEW]: '/hub-v2/onboarding-v2/onboarding-payroll-v2/queue',
    [OnboardingTaskTemplateName.PAYROLL_MIGRATION]: '/hub-v2/onboarding-v2/onboarding-payroll-v2/queue',
    // employer registration (task framework tasks live in v1 ER queue Task Framework tab)
    [OnboardingTaskTemplateName.WAITING_TO_PROCESS_PAYROLL]: '/hub-v2/onboarding/employer-registration/queue',
    [OnboardingTaskTemplateName.PAYROLL_PROCESSED_ER_DECISION]: '/hub-v2/onboarding/employer-registration/queue',
    [OnboardingTaskTemplateName.ER_CHECKPOINT]: '/hub-v2/onboarding/employer-registration/queue',
    [OnboardingTaskTemplateName.ER_NOT_REQUIRED]: '/hub-v2/onboarding/employer-registration/queue',
    [OnboardingTaskTemplateName.ER_IN_PROCESS]: '/hub-v2/onboarding/employer-registration/queue',
    [OnboardingTaskTemplateName.ER_AWAITING_MEMBER]: '/hub-v2/onboarding/employer-registration/queue',
    [OnboardingTaskTemplateName.ER_WAITING_FOR_ID]: '/hub-v2/onboarding/employer-registration/queue',
    [OnboardingTaskTemplateName.ER_TPA]: '/hub-v2/onboarding/employer-registration/queue',
    [OnboardingTaskTemplateName.ER_CLOSE]: '/hub-v2/onboarding/employer-registration/queue',
    // formation
    [OnboardingTaskTemplateName.COMPLETE_APPLICATION]: '/hub-v2/onboarding-v2/formation-v2/queue',
    [OnboardingTaskTemplateName.FILE_LLC]: '/hub-v2/onboarding-v2/formation-v2/queue',
    [OnboardingTaskTemplateName.CONFIRM_LLC]: '/hub-v2/onboarding-v2/formation-v2/queue',
    [OnboardingTaskTemplateName.FILE_EIN]: '/hub-v2/onboarding-v2/formation-v2/queue',
    // Palm Cases and Review Application are worked on Formation V2; the Palm Formations board is a
    // read-only monitor and lists neither, so their return paths land on V2's work queue.
    [OnboardingTaskTemplateName.RESPOND_TO_FORMATION_CASE]: '/hub-v2/onboarding-v2/formation-v2/queue',
    [OnboardingTaskTemplateName.REVIEW_FORMATION_APPLICATION]: '/hub-v2/onboarding-v2/formation-v2/queue',
    // formation exception review
    [OnboardingTaskTemplateName.MEMBER_ENTITY_FORMATION_DQ]: '/hub-v2/onboarding-v2/formation-v2/queue',
    [OnboardingTaskTemplateName.MEMBER_ENTITY_FORMATION_BLOCKED]: '/hub-v2/onboarding-v2/formation-v2/queue',
    // takeover exception review
    [OnboardingTaskTemplateName.MEMBER_ENTITY_TAKEOVER_DQ]: '/hub-v2/onboarding-v2/takeover-v2/queue',
    [OnboardingTaskTemplateName.MEMBER_ENTITY_TAKEOVER_BLOCKED]: '/hub-v2/onboarding-v2/takeover-v2/queue',
    [OnboardingTaskTemplateName.MEMBER_ENTITY_TAKEOVER_MIDDESK_IRS_UNAVAILABLE]:
        '/hub-v2/onboarding-v2/takeover-v2/queue',
    [OnboardingTaskTemplateName.REVIEW_MIDDESK_EXCEPTION]: '/hub-v2/onboarding-v2/takeover-v2/queue',
    // scorp filing
    [OnboardingTaskTemplateName.REVIEW_SCORP_ELECTION_DOCS]: '/hub-v2/onboarding-v2/scorp-filing-v2',
    [OnboardingTaskTemplateName.FAX_SCORP_ELECTION_DOCS]: '/hub-v2/onboarding-v2/scorp-filing-v2/queue',
    [OnboardingTaskTemplateName.CONFIRM_SCORP_ELECTION_DOCS_FAX]: '/hub-v2/onboarding-v2/scorp-filing-v2/queue',
    // takeover
    [OnboardingTaskTemplateName.COMPLETE_ENTITY_TAKEOVER_APPLICATION]: '/hub-v2/onboarding-v2/takeover-v2/queue',
    [OnboardingTaskTemplateName.VERIFY_EIN_CONFIRMATION_LETTER]: '/hub-v2/onboarding-v2/takeover-v2/queue',
    [OnboardingTaskTemplateName.VERIFY_SCORP_APPROVAL_LETTER]: '/hub-v2/onboarding-v2/takeover-v2/queue',
    [OnboardingTaskTemplateName.VERIFY_BUSINESS]: '/hub-v2/onboarding-v2/takeover-v2/queue',
    [OnboardingTaskTemplateName.VERIFY_ARTICLES_OF_ORGANIZATION]: '/hub-v2/onboarding-v2/takeover-v2/queue',
    [OnboardingTaskTemplateName.REFUND_BACK_BOOKS]: BACK_BOOKS_REFUND_QUEUE_PATH,
    // BTR v2 tasks
    [TaxTaskTemplateName.E_FILE_EXTENSION]: '/hub-v2/tax/btr-v2/queue',
    [TaxTaskTemplateName.AWAITING_GOVERNMENT_ACKNOWLEDGEMENT_OF_EXTENSION]: '/hub-v2/tax/btr-v2/queue',
    [TaxTaskTemplateName.E_FILE_REJECTION_OF_EXTENSION]: '/hub-v2/tax/btr-v2/queue',
    [TaxTaskTemplateName.PAPER_FILE_OF_EXTENSION]: '/hub-v2/tax/btr-v2/queue',
    [TaxTaskTemplateName.RECEIPT_OF_BUSINESS_TAX_ORGANIZER]: '/hub-v2/tax/btr-v2/queue',
    [TaxTaskTemplateName.PREPARATION_OF_TAX_RETURN]: '/hub-v2/tax/btr-v2/queue',
    [TaxTaskTemplateName.REVIEW_OF_TAX_RETURN]: '/hub-v2/tax/btr-v2/queue',
    [TaxTaskTemplateName.MEMBER_APPROVAL_OF_TAX_RETURN]: '/hub-v2/tax/btr-v2/queue',
    [TaxTaskTemplateName.E_FILE_OF_TAX_RETURN]: '/hub-v2/tax/btr-v2/queue',
    [TaxTaskTemplateName.AWAITING_GOVERNMENT_ACKNOWLEDGEMENT_OF_TAX_RETURN]: '/hub-v2/tax/btr-v2/queue',
    [TaxTaskTemplateName.E_FILE_REJECTION_OF_TAX_RETURN]: '/hub-v2/tax/btr-v2/queue',
    [TaxTaskTemplateName.PAPER_FILE_OF_TAX_RETURN]: '/hub-v2/tax/btr-v2/queue',
};

export const FORMATION_EXCEPTION_REVIEW_TASK_TEMPLATE_NAMES = [
    OnboardingTaskTemplateName.MEMBER_ENTITY_FORMATION_DQ,
    OnboardingTaskTemplateName.MEMBER_ENTITY_FORMATION_BLOCKED,
];

export const TAKEOVER_EXCEPTION_REVIEW_TASK_TEMPLATE_NAMES = [
    OnboardingTaskTemplateName.MEMBER_ENTITY_TAKEOVER_DQ,
    OnboardingTaskTemplateName.MEMBER_ENTITY_TAKEOVER_BLOCKED,
    OnboardingTaskTemplateName.MEMBER_ENTITY_TAKEOVER_MIDDESK_IRS_UNAVAILABLE,
];

export const ONBOARDING_FORMATION_TASK_TEMPLATE_NAMES = [
    OnboardingTaskTemplateName.FILE_LLC,
    OnboardingTaskTemplateName.CONFIRM_LLC,
    OnboardingTaskTemplateName.FILE_EIN,
    ...FORMATION_EXCEPTION_REVIEW_TASK_TEMPLATE_NAMES,
];

// Palm-era Formation V2 queue: the vendor now owns the work exception review used to catch, so those
// templates drop out and the two Palm templates come in. Gated by PalmFormationsHubView.
export const ONBOARDING_FORMATION_PALM_TASK_TEMPLATE_NAMES = [
    OnboardingTaskTemplateName.FILE_LLC,
    OnboardingTaskTemplateName.CONFIRM_LLC,
    OnboardingTaskTemplateName.FILE_EIN,
    OnboardingTaskTemplateName.RESPOND_TO_FORMATION_CASE,
    OnboardingTaskTemplateName.REVIEW_FORMATION_APPLICATION,
];

// Set on vendor-owned formation milestone tasks
export const VENDOR_FORMATION_TASK_LABEL = 'formation_vendor';

// export const ONBOARDING_TASK_TEMPLATE_NAMES = Object.keys(ONBOARDING_TASK_TEMPLATE_NAMES_TO_LABELS);

export const ONBOARDING_ACCOUNTANT_TASK_TEMPLATE_NAMES = [OnboardingTaskTemplateName.ESTABLISH_BEGINNING_BALANCE];

export const ONBOARDING_SCORP_FILING_TASK_TEMPLATE_NAMES = [
    OnboardingTaskTemplateName.REVIEW_SCORP_ELECTION_DOCS,
    OnboardingTaskTemplateName.FAX_SCORP_ELECTION_DOCS,
    OnboardingTaskTemplateName.CONFIRM_SCORP_ELECTION_DOCS_FAX,
];

export const ONBOARDING_TAKEOVER_TASK_TEMPLATE_NAMES = [
    OnboardingTaskTemplateName.VERIFY_EIN_CONFIRMATION_LETTER,
    OnboardingTaskTemplateName.VERIFY_SCORP_APPROVAL_LETTER,
    OnboardingTaskTemplateName.VERIFY_BUSINESS,
    OnboardingTaskTemplateName.VERIFY_ARTICLES_OF_ORGANIZATION,
    ...TAKEOVER_EXCEPTION_REVIEW_TASK_TEMPLATE_NAMES,
];

export const ONBOARDING_TAKEOVER_MIDDESK_TASK_TEMPLATE_NAMES = [OnboardingTaskTemplateName.REVIEW_MIDDESK_EXCEPTION];

// A list, not a bare constant: the queue collects refund work, so further refund templates join
// here rather than getting a queue each.
export const REFUND_TASK_TEMPLATE_NAMES = [OnboardingTaskTemplateName.REFUND_BACK_BOOKS];

export const ONBOARDING_PAYROLL_TASK_TEMPLATE_NAMES = [
    OnboardingTaskTemplateName.BENEFITS_ON_PAYROLL,
    OnboardingTaskTemplateName.GUSTO_REVIEW,
    OnboardingTaskTemplateName.PAYROLL_MIGRATION,
];

// The task templates that make up an employer registration, in stage order.
export const NEW_ER_OTF_TASK_TEMPLATE_NAMES = [
    OnboardingTaskTemplateName.WAITING_TO_PROCESS_PAYROLL,
    OnboardingTaskTemplateName.PAYROLL_PROCESSED_ER_DECISION,
    OnboardingTaskTemplateName.ER_CHECKPOINT,
    OnboardingTaskTemplateName.ER_NOT_REQUIRED,
    OnboardingTaskTemplateName.ER_IN_PROCESS,
    OnboardingTaskTemplateName.ER_AWAITING_MEMBER,
    OnboardingTaskTemplateName.ER_WAITING_FOR_ID,
    OnboardingTaskTemplateName.ER_TPA,
    OnboardingTaskTemplateName.ER_CLOSE,
];
